import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import Button                    from '@widesk-ui/components/Button';
import antdTheme                 from 'antd/lib/theme';
import { boxBetween }            from '@/TokamapEditor/helpers/planAnalysisRegion';
import { isClickRatherThanDrag } from '@/TokamapEditor/helpers/planAnalysisRegion';
import { fullView }              from '@/TokamapEditor/helpers/usePlanViewport';
import { usePlanViewport }       from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanPoint }        from '@/TokamapEditor/helpers/usePlanViewport';
import PlanZoomControls          from '@/TokamapEditor/ux/PlanZoomControls';
import type { PlanRegion }       from '@models/map/dxfTypes';
import type { PlanSourceResult } from '@models/map/dxfTypes';

type Props = {
	source: PlanSourceResult;
	region: PlanRegion | null;
	disabled?: boolean;
	onChange: (region: PlanRegion) => void;
	children?: React.ReactNode;
};

type Mode = 'draw' | 'pan';

const MIDDLE_BUTTON = 1;

const PlanRegionCanvas = ({ source, region, disabled, onChange, children }: Props) => {
	const { token } = antdTheme.useToken();
	const viewport = usePlanViewport(source, () => fullView(source));
	const [mode, setMode] = React.useState<Mode>('draw');
	const [origin, setOrigin] = React.useState<{ plan: PlanPoint; client: PlanPoint } | null>(null);
	const [tracing, setTracing] = React.useState<PlanRegion | null>(null);

	const clampToPlan = (point: PlanPoint): PlanPoint => ({
		x: Math.min(Math.max(point.x, 0), source.width),
		y: Math.min(Math.max(point.y, 0), source.height),
	});

	const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
		event.currentTarget.setPointerCapture(event.pointerId);

		if (disabled || 'pan' === mode || MIDDLE_BUTTON === event.button) {
			viewport.startPan(event);

			return;
		}

		const point = viewport.toPlan(event.clientX, event.clientY);
		if (!point) return;

		const plan = clampToPlan(point);
		setOrigin({ plan, client: { x: event.clientX, y: event.clientY } });
		setTracing({ minX: plan.x, minY: plan.y, maxX: plan.x, maxY: plan.y });
	};

	const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
		if (viewport.movePan(event) || !origin) return;

		const point = viewport.toPlan(event.clientX, event.clientY);
		if (point) setTracing(boxBetween(origin.plan, clampToPlan(point)));
	};

	const onPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
		viewport.endPan();
		if (!origin || !tracing) return;

		const onScreen = boxBetween(origin.client, { x: event.clientX, y: event.clientY });
		if (!isClickRatherThanDrag(onScreen)) onChange(tracing);

		setOrigin(null);
		setTracing(null);
	};

	const { view } = viewport;
	const shown = tracing ?? region;

	return (
		<View flex style={{ position: 'relative', background: token.colorWhite, overflow: 'hidden' }}>
			<svg
				ref={viewport.svgRef}
				viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
				style={{
					width: '100%',
					height: '100%',
					touchAction: 'none',
					cursor: viewport.panning ? 'grabbing' : ('pan' === mode || disabled) ? 'grab' : 'crosshair',
				}}
				onWheel={viewport.onWheel}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onAuxClick={event => event.preventDefault()}
			>
				<image href={source.imageDataUri} x={0} y={0} width={source.width} height={source.height} />

				{shown && (
					<>
						<path
							d={`M0,0 H${source.width} V${source.height} H0 Z`
								+ ` M${shown.minX},${shown.minY} H${shown.maxX} V${shown.maxY} H${shown.minX} Z`}
							fillRule="evenodd"
							fill={token.colorBgMask}
							pointerEvents="none"
						/>
						<rect
							x={shown.minX}
							y={shown.minY}
							width={shown.maxX - shown.minX}
							height={shown.maxY - shown.minY}
							fill="none"
							stroke={token.colorPrimary}
							strokeWidth={2}
							vectorEffect="non-scaling-stroke"
							pointerEvents="none"
						/>
					</>
				)}
			</svg>

			<View
				row
				gap={4}
				style={{
					position: 'absolute',
					top: 12,
					left: 12,
					background: token.colorBgElevated,
					padding: 4,
					borderRadius: 6,
					boxShadow: token.boxShadowSecondary,
				}}
			>
				<Button size="small" disabled={disabled} type={'draw' === mode ? 'primary' : 'default'} onClick={() => setMode('draw')}>
					Tracer la zone
				</Button>
				<Button size="small" type={'pan' === mode ? 'primary' : 'default'} onClick={() => setMode('pan')}>
					Se déplacer
				</Button>
			</View>

			<PlanZoomControls
				zoomPercent={viewport.zoomPercent}
				onZoomIn={viewport.zoomIn}
				onZoomOut={viewport.zoomOut}
				onFit={viewport.fit}
			/>

			{children}
		</View>
	);
};

export default PlanRegionCanvas;
